import { Injectable, inject, signal } from '@angular/core';

import { Observable, catchError, of, shareReplay, tap, throwError } from 'rxjs';

import { Package, PackageCoverageReference } from '../models/package-configurator/package.model';
import { PackageConfiguratorService } from '../services/package-configurator.service';

/**
 * Root cache for the packages list. The list is fetched from the backend once and reused across the
 * packages section: `load()` returns it from the store when present, otherwise makes a single request
 * (shared so concurrent callers don't trigger duplicate calls). Packages created or edited in the
 * configurator are written back with `upsert()` / `setCoverages()`, so the list stays current without
 * refetching.
 */
@Injectable({ providedIn: 'root' })
export class PackagesStore {
  private readonly service = inject(PackageConfiguratorService);

  private readonly packagesSignal = signal<Package[]>([]);
  private loaded = false;
  /** In-flight/completed shared request — guards against parallel loads issuing multiple calls. */
  private request$?: Observable<Package[]>;

  /** Cached packages (empty until the first successful load). */
  readonly packages = this.packagesSignal.asReadonly();

  /** Packages from the store; fetches once if not loaded yet, and reuses the cache afterwards. */
  load(): Observable<Package[]> {
    if (this.loaded) return of(this.packagesSignal());

    this.request$ ??= this.service.getPackages().pipe(
      tap(packages => {
        this.packagesSignal.set(packages);
        this.loaded = true;
      }),
      catchError(error => {
        // Drop the cached request so a later load() can retry after a failure.
        this.request$ = undefined;
        return throwError(() => error);
      }),
      shareReplay(1)
    );

    return this.request$;
  }

  /** Adds a newly created package, or replaces a known one with its latest saved version. */
  upsert(pkg: Package): void {
    this.packagesSignal.update(packages => {
      const existing = packages.find(item => item.id === pkg.id);
      // Save responses may omit the coverage references — keep the known ones rather than dropping them.
      const merged: Package = { ...existing, ...pkg, coverages: pkg.coverages ?? existing?.coverages };

      return existing ? packages.map(item => (item.id === pkg.id ? merged : item)) : [...packages, merged];
    });
  }

  /** Replaces a known package's coverage references after its contents were created, rescheduled or cancelled. */
  setCoverages(packageId: string, coverages: PackageCoverageReference[]): void {
    this.packagesSignal.update(packages => packages.map(item => (item.id === packageId ? { ...item, coverages } : item)));
  }
}
